import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { bulkActionOnChats, BulkActionPayload } from '../requests/bulk-action';
import { bulkActionProgressAtom } from '../states/bulk-action-progress';
import {
  chatListAtom,
  chatFilterStatusCountsAtom,
  isAllChatsSelectedAtom,
  selectedChatIdsListAtom,
  excludedChatIdsAtom,
} from '../states/chat-list';
import { resolveFilterType, getTotalFilterCount } from '../utils/chat-filter-count';
import { getCurrentUserId } from '../../../../infra/firebase/init';

export function useBulkActionOnChats() {
  const setBulkActionProgress = useSetAtom(bulkActionProgressAtom);
  const isAllChatsSelected = useAtomValue(isAllChatsSelectedAtom);
  const selectedChatIdsList = useAtomValue(selectedChatIdsListAtom);
  const excludedChatIds = useAtomValue(excludedChatIdsAtom);
  const { data: filterCounts } = useAtomValue(chatFilterStatusCountsAtom);
  const chatFilters = useAtomValue(chatListAtom);

  const getSelectedTotal = useCallback(() => {
    if (!isAllChatsSelected) return selectedChatIdsList.length;
    const filterType = resolveFilterType({
      slaStatus: chatFilters.slaStatus,
      assignedUserId: chatFilters.assignedUserId,
      currentUserId: getCurrentUserId(),
    });
    const total = getTotalFilterCount({ filterCounts, filterType, status: chatFilters.status });
    return Math.max(0, (total ?? 0) - excludedChatIds.length);
  }, [isAllChatsSelected, selectedChatIdsList.length, excludedChatIds, filterCounts, chatFilters]);

  return useCallback(
    async (payload: BulkActionPayload) => {
      const total = getSelectedTotal();
      const response = await bulkActionOnChats(payload);
      setBulkActionProgress((prev) => {
        if (prev.some((op) => op.operationId === response.operationId)) return prev;
        return [
          {
            operationId: response.operationId,
            status: 'IN_PROGRESS',
            action: payload.action,
            total,
            succeeded: 0,
            failed: 0,
          },
          ...prev,
        ];
      });
      return response;
    },
    [setBulkActionProgress, getSelectedTotal]
  );
}
